import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import useAccountLookup from "../hooks/use_account_lookup";
import AccountNumberCell from "./AccountNumberCell";
import { fetchMissingUtilityCredits, mergeMissingCredits, creditSourceLabel } from "../hooks/missing_utility_credits";

// Payment type from the account number (the M-Pesa service applies the same
// rules to payments; these are used for the meter credits):
//   power - NCBA account numbers of more than 10 digits starting with 1
//   lease - starts with 6
//   levy  - starts with 5
// Water is matched against the facility's water accounts.
const ACCOUNT_PATTERNS = {
  power: /^1\d{10,}$/,
  lease: /^6/,
  levy: /^5/,
};

// Backend route that reads bank payments from the M-Pesa service
const BANK_PAYMENTS_URL = "/api/app/payment_management/bank/get_bank_payments";

const UTILITY_TABS = [
  { key: "all", label: "All", icon: "fa-list", badge: "bg-secondary" },
  { key: "lease", label: "Lease", icon: "fa-home", badge: "bg-primary" },
  { key: "levy", label: "Levy", icon: "fa-receipt", badge: "bg-info" },
  { key: "power", label: "Power", icon: "fa-bolt", badge: "bg-warning" },
  { key: "water", label: "Water", icon: "fa-tint", badge: "bg-success" },
];

const EMPTY_FIELD_FILTERS = { search: "", accountNumber: "", paymentRef: "", amount: "" };

const EMPTY_STATS = {
  totalPayments: 0,
  totalAmount: 0,
  todayCount: 0,
  todayAmount: 0,
  weeklyCount: 0,
  weeklyAmount: 0,
  monthlyCount: 0,
  monthlyAmount: 0,
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDateTime = (value) => {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())} ${MONTH_NAMES[d.getMonth()]} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const formatCurrency = (val) =>
  `KES ${parseFloat(val || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Start of today, this week (Sunday) and this month in the browser's timezone
const periodStarts = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const week = new Date(today);
  week.setDate(today.getDate() - today.getDay());
  const month = new Date(now.getFullYear(), now.getMonth(), 1);
  return { today, week, month };
};

const BankPayments = ({ showDashboard = true }) => {
  const { getAccount, openAccount, getAccountNumbers } = useAccountLookup();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dateFilters, setDateFilters] = useState({ startDate: null, endDate: null });
  const [fieldFilters, setFieldFilters] = useState(EMPTY_FIELD_FILTERS);
  const [activeTab, setActiveTab] = useState("all"); // a UTILITY_TABS key
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [stats, setStats] = useState(EMPTY_STATS);
  const [tabCounts, setTabCounts] = useState({});

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
  });

  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: "createdAt",
    sortOrder: -1,
  });

  const dt = useRef(null);
  const requestId = useRef(0); // ignore responses from superseded requests

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyState.page, lazyState.rows, dateFilters, fieldFilters, activeTab]);

  const resetToFirstPage = () => setLazyState((prev) => ({ ...prev, page: 1, first: 0 }));

  // Query for one tab. The backend resolves water to the facility's water
  // accounts; power, lease and levy are recognised from the account number.
  const buildQuery = (tab, page, limit, starts) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      todayStart: starts.today.toISOString(),
      weekStart: starts.week.toISOString(),
      monthStart: starts.month.toISOString(),
    });
    if (dateFilters.startDate)
      params.append("startDate", dateFilters.startDate.toISOString().split("T")[0]);
    if (dateFilters.endDate)
      params.append("endDate", dateFilters.endDate.toISOString().split("T")[0]);
    Object.entries(fieldFilters).forEach(([key, value]) => {
      if (String(value).trim()) params.append(key, String(value).trim());
    });
    if (tab !== "all") params.append("utility", tab);
    return params;
  };

  const fetchPage = async (facilityId, params) => {
    const response = await makeRequest2(`${BANK_PAYMENTS_URL}/${facilityId}?${params}`, "GET");
    if (!response?.success) throw new Error(response?.error || "Failed to fetch bank payments");
    return response.data;
  };

  const fetchPayments = async () => {
    const current = ++requestId.current;
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      const starts = periodStarts();
      // Only needed to sort the meter credits below; the backend filters payments
      const waterAccounts = await getAccountNumbers("water");

      const creditMatchesTab = (tab) => (credit) => {
        const accountNumber = String(credit.accountNumber || "");
        if (ACCOUNT_PATTERNS[tab]) return ACCOUNT_PATTERNS[tab].test(accountNumber);
        if (tab === "water") return waterAccounts.includes(accountNumber);
        return true;
      };

      // The selected tab's page, a count for each other tab, and the power/water
      // credits added without a matching bank payment (e.g. a manually uploaded
      // NCBA payment).
      const otherTabs = UTILITY_TABS.map((t) => t.key).filter((key) => key !== activeTab);
      const [data, otherCounts, credits] = await Promise.all([
        fetchPage(facilityId, buildQuery(activeTab, lazyState.page, lazyState.rows, starts)),
        Promise.all(
          otherTabs.map((tab) =>
            fetchPage(facilityId, buildQuery(tab, 1, 1, starts))
              .then((d) => d.pagination?.totalCount || 0)
              .catch(() => 0),
          ),
        ),
        fetchMissingUtilityCredits({
          facilityId,
          channel: "bank",
          startDate: dateFilters.startDate,
          endDate: dateFilters.endDate,
          search: fieldFilters.search,
        }),
      ]);
      if (current !== requestId.current) return;

      const matchingCredits = credits.filter(creditMatchesTab(activeTab));
      setPayments(mergeMissingCredits(data.payments || [], matchingCredits, data.pagination));
      if (data.pagination) setPagination(data.pagination);

      // Server totals over every matching bank payment, plus the matching credits
      // (that list is not paged, so it is already complete).
      const summary = data.summary || {};
      const totals = { ...EMPTY_STATS };
      Object.keys(totals).forEach((key) => {
        totals[key] = summary[key] || 0;
      });
      totals.totalPayments = (data.pagination?.totalCount || 0) + matchingCredits.length;
      matchingCredits.forEach((c) => {
        const date = new Date(c.createdAt);
        const amount = parseFloat(c.amount || 0);
        totals.totalAmount += amount;
        if (date >= starts.today) { totals.todayCount++; totals.todayAmount += amount; }
        if (date >= starts.week) { totals.weeklyCount++; totals.weeklyAmount += amount; }
        if (date >= starts.month) { totals.monthlyCount++; totals.monthlyAmount += amount; }
      });
      setStats(totals);

      const counts = { [activeTab]: totals.totalPayments };
      otherTabs.forEach((tab, i) => {
        counts[tab] = otherCounts[i] + credits.filter(creditMatchesTab(tab)).length;
      });
      setTabCounts(counts);
    } catch (err) {
      console.error("Error fetching bank payments:", err);
      if (current === requestId.current) {
        setPayments([]);
        setStats(EMPTY_STATS);
      }
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    resetToFirstPage();
  };

  const handleFieldFilterChange = (field, value) => {
    setFieldFilters((prev) => ({ ...prev, [field]: value }));
    resetToFirstPage();
  };

  const handleDateFilterChange = (field, value) => {
    setDateFilters((prev) => ({ ...prev, [field]: value }));
    resetToFirstPage();
  };

  const applyQuickDateFilter = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    setDateFilters({ startDate, endDate });
    resetToFirstPage();
  };

  const clearAllFilters = () => {
    setDateFilters({ startDate: null, endDate: null });
    setFieldFilters(EMPTY_FIELD_FILTERS);
    resetToFirstPage();
  };

  const hasActiveFilters =
    dateFilters.startDate ||
    dateFilters.endDate ||
    Object.values(fieldFilters).some((v) => String(v).trim());

  const onPage = (event) => {
    setLazyState((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    }));
  };

  const onSort = (event) => {
    setLazyState((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  // ── Exports (the rows currently shown) ──────────────────────────────────────

  const exportRows = () =>
    [...payments]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map((p) => ({
        "Payment Reference": p.paymentRef || "",
        "Account Number": p.accountNumber || "",
        Unit: getAccount(p.accountNumber)?.unitName || "",
        "Customer Name": p.customerName || "",
        Amount: parseFloat(p.amount || 0),
        "Date & Time": formatDateTime(p.createdAt),
      }));

  const exportFileName = (ext) =>
    `bank-payments-${activeTab}-${new Date().toISOString().split("T")[0]}.${ext}`;

  const downloadBlob = (blob, fileName) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCSV = () => {
    const rows = exportRows();
    const headers = Object.keys(rows[0]);
    const escape = (v) =>
      typeof v === "string" && (v.includes(",") || v.includes('"')) ? `"${v.replace(/"/g, '""')}"` : v;
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers.map((h) => escape(h === "Amount" ? row[h].toFixed(2) : row[h])).join(","),
      ),
    ].join("\n");
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), exportFileName("csv"));
  };

  const exportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default();
        const tabLabel = UTILITY_TABS.find((t) => t.key === activeTab)?.label || "All";
        doc.setFontSize(18);
        doc.text(`Bank Payments Report - ${tabLabel}`, 14, 22);
        doc.setFontSize(11);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);

        const rows = exportRows().map((row) => ({ ...row, Amount: formatCurrency(row.Amount) }));
        doc.autoTable({
          head: [Object.keys(rows[0])],
          body: rows.map((row) => Object.values(row)),
          startY: 40,
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [245, 245, 245] },
        });
        doc.save(exportFileName("pdf"));
      });
    });
  };

  const exportExcel = () => {
    import("xlsx")
      .then((xlsx) => {
        const worksheet = xlsx.utils.json_to_sheet(exportRows());
        worksheet["!cols"] = [{ wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 25 }, { wch: 12 }, { wch: 20 }];
        const workbook = { Sheets: { "Bank Payments": worksheet }, SheetNames: ["Bank Payments"] };
        const buffer = xlsx.write(workbook, { bookType: "xlsx", type: "array" });
        downloadBlob(
          new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
          }),
          exportFileName("xlsx"),
        );
      })
      .catch((error) => {
        console.error("Error loading xlsx library:", error);
        alert("Failed to export Excel file. Please try again.");
      });
  };

  // ── Cell templates ──────────────────────────────────────────────────────────

  const refTemplate = (rowData) => (
    <span>
      {rowData.paymentRef || "—"}
      {creditSourceLabel(rowData) && (
        <span
          className="badge bg-info ms-2"
          title="Added as a meter credit with no matching bank payment"
        >
          {creditSourceLabel(rowData)}
        </span>
      )}
    </span>
  );

  const accountTemplate = (rowData) => (
    <AccountNumberCell
      accountNumber={rowData.accountNumber}
      getAccount={getAccount}
      openAccount={openAccount}
    />
  );

  const unitTemplate = (rowData) => {
    const account = getAccount(rowData.accountNumber);
    if (!account?.unitName) return "—";
    if (!account.unitId) return account.unitName;
    return (
      <Link
        to={`/facility/unit_management/view_unit/${account.unitId}`}
        className="text-decoration-underline"
        title={`Open unit ${account.unitName}`}
      >
        {account.unitName}
      </Link>
    );
  };

  const amountTemplate = (rowData) => formatCurrency(rowData.amount);
  const dateTemplate = (rowData) => formatDateTime(rowData.createdAt);

  // ── Layout (matches the M-Pesa Transactions tab) ────────────────────────────

  const renderHeader = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="d-flex align-items-center gap-2">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={fieldFilters.search}
            onChange={(e) => handleFieldFilterChange("search", e.target.value)}
            placeholder="Quick search..."
            className="form-control"
          />
        </span>
        <Button
          type="button"
          icon={showAdvancedFilters ? "pi pi-angle-up" : "pi pi-angle-down"}
          label="Filters"
          className="p-button-outlined p-button-sm"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
        />
        <Button
          type="button"
          icon="pi pi-times"
          label="Clear"
          className="p-button-outlined p-button-sm"
          onClick={clearAllFilters}
          disabled={!hasActiveFilters}
        />
      </div>
      <div className="d-flex align-items-center">
        <div className="btn-group me-2">
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={exportCSV}
            disabled={loading || payments.length === 0}
            title="Export as CSV"
          >
            <i className="fa fa-file-csv me-1"></i>
            CSV
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={exportPdf}
            disabled={loading || payments.length === 0}
            title="Export as PDF"
          >
            <i className="fa fa-file-pdf me-1"></i>
            PDF
          </button>
          <button
            type="button"
            className="btn btn-info btn-sm"
            onClick={exportExcel}
            disabled={loading || payments.length === 0}
            title="Export as Excel"
          >
            <i className="fa fa-file-excel me-1"></i>
            Excel
          </button>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => fetchPayments()} disabled={loading}>
          <i className="fa fa-refresh me-1"></i>
          Refresh
        </button>
      </div>
    </div>
  );

  const renderAdvancedFilters = () => {
    if (!showAdvancedFilters) return null;

    return (
      <Card className="mb-3">
        <div className="row g-3">
          {/* Date Range Filters */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h6 className="card-title">📅 Date Range</h6>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">From Date</label>
                    <Calendar
                      value={dateFilters.startDate}
                      onChange={(e) => handleDateFilterChange("startDate", e.value)}
                      placeholder="Start date"
                      dateFormat="yy-mm-dd"
                      showIcon
                      className="w-100"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">To Date</label>
                    <Calendar
                      value={dateFilters.endDate}
                      onChange={(e) => handleDateFilterChange("endDate", e.value)}
                      placeholder="End date"
                      dateFormat="yy-mm-dd"
                      showIcon
                      className="w-100"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <small className="text-muted">Quick filters: </small>
                  <button className="btn btn-outline-primary btn-sm me-1" onClick={() => applyQuickDateFilter(1)}>
                    Today
                  </button>
                  <button className="btn btn-outline-primary btn-sm me-1" onClick={() => applyQuickDateFilter(7)}>
                    7 Days
                  </button>
                  <button className="btn btn-outline-primary btn-sm me-1" onClick={() => applyQuickDateFilter(30)}>
                    30 Days
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Field-Specific Filters */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h6 className="card-title">🔍 Specific Filters</h6>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">Account Number</label>
                    <InputText
                      value={fieldFilters.accountNumber}
                      onChange={(e) => handleFieldFilterChange("accountNumber", e.target.value)}
                      placeholder="Filter by account"
                      className="w-100"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Payment Reference</label>
                    <InputText
                      value={fieldFilters.paymentRef}
                      onChange={(e) => handleFieldFilterChange("paymentRef", e.target.value)}
                      placeholder="Filter by reference"
                      className="w-100"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Amount</label>
                    <InputText
                      value={fieldFilters.amount}
                      onChange={(e) => handleFieldFilterChange("amount", e.target.value)}
                      placeholder="Exact amount"
                      className="w-100"
                      keyfilter="pnum"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Results per page</label>
                    <Dropdown
                      value={lazyState.rows}
                      options={[
                        { label: "10", value: 10 },
                        { label: "25", value: 25 },
                        { label: "50", value: 50 },
                        { label: "100", value: 100 },
                      ]}
                      onChange={(e) =>
                        setLazyState((prev) => ({ ...prev, rows: e.value, first: 0, page: 1 }))
                      }
                      className="w-100"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  const statCard = ({ label, value, icon, color, col }) => (
    <div className={`${col} mb-3`} key={label}>
      <div className={`card h-100 border-left-${color} shadow`}>
        <div className="card-body">
          <div className="row no-gutters align-items-center">
            <div className="col mr-2">
              <div className={`text-xs font-weight-bold text-${color} text-uppercase mb-1`}>{label}</div>
              <div className="h5 mb-0 font-weight-bold text-gray-800">{value}</div>
            </div>
            <div className="col-auto">
              <i className={`fa ${icon} fa-2x text-gray-300`}></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStatisticsDashboard = () => {
    if (!showDashboard) return null;
    const quarter = "col-md-3 col-sm-6";
    const half = "col-md-6";
    const average = stats.totalPayments > 0 ? stats.totalAmount / stats.totalPayments : 0;

    return (
      <div className="statistics-dashboard mb-4">
        <div className="row">
          {statCard({ label: "Total Transactions", value: stats.totalPayments.toLocaleString(), icon: "fa-calendar", color: "primary", col: quarter })}
          {statCard({ label: "Total Amount", value: formatCurrency(stats.totalAmount), icon: "fa-money-bill", color: "success", col: quarter })}
          {statCard({ label: "Today's Transactions", value: `${stats.todayCount} (${formatCurrency(stats.todayAmount)})`, icon: "fa-money-bill-wave", color: "info", col: quarter })}
          {statCard({ label: "Monthly Transactions", value: `${stats.monthlyCount} (${formatCurrency(stats.monthlyAmount)})`, icon: "fa-chart-line", color: "warning", col: quarter })}
        </div>
        <div className="row">
          {statCard({ label: "Weekly Transactions", value: `${stats.weeklyCount} (${formatCurrency(stats.weeklyAmount)})`, icon: "fa-calendar-week", color: "danger", col: half })}
          {statCard({ label: "Average Transaction Value", value: formatCurrency(average), icon: "fa-calculator", color: "secondary", col: half })}
        </div>
      </div>
    );
  };

  const renderUtilityTabs = () => (
    <div className="mb-4">
      <ul className="nav nav-tabs" role="tablist">
        {UTILITY_TABS.map((tab) => (
          <li className="nav-item" role="presentation" key={tab.key}>
            <button
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => handleTabChange(tab.key)}
            >
              <span className="d-flex align-items-center gap-2">
                <i className={`fa ${tab.icon} me-1`}></i>
                {tab.label}
                <span className={`badge ${tab.badge} rounded-pill`}>{tabCounts[tab.key] || 0}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderPaginationInfo = () => {
    const start = lazyState.first + 1;
    const end = Math.min(lazyState.first + lazyState.rows, pagination.totalCount);
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <small className="text-muted">
            Showing {start} to {end} of {pagination.totalCount} entries
            {hasActiveFilters && (
              <span className="ms-2">
                <i className="fa fa-filter text-primary"></i> Filtered
              </span>
            )}
          </small>
        </div>
        <div>
          <small className="text-muted">
            Page {pagination.currentPage} of {pagination.totalPages}
          </small>
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Bank Payments</h5>
      </div>
      <div className="card-body">
        {renderStatisticsDashboard()}

        {renderUtilityTabs()}

        {renderAdvancedFilters()}

        {payments.length === 0 && !loading ? (
          <>
            {renderHeader()}
            <div className="alert alert-info">
              <i className="fa fa-info-circle me-2"></i>
              No bank payments found for this category.
            </div>
          </>
        ) : (
          <DataTable
            ref={dt}
            value={payments}
            lazy
            paginator
            first={lazyState.first}
            rows={lazyState.rows}
            totalRecords={pagination.totalCount}
            onPage={onPage}
            onSort={onSort}
            sortField={lazyState.sortField}
            sortOrder={lazyState.sortOrder}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            dataKey="_id"
            loading={loading}
            header={renderHeader()}
            emptyMessage="No payments found"
            className="p-datatable-sm"
          >
            <Column field="paymentRef" header="Payment Reference" body={refTemplate} sortable />
            <Column field="accountNumber" header="Account Number" body={accountTemplate} sortable />
            <Column header="Unit" body={unitTemplate} />
            <Column field="customerName" header="Customer Name" sortable />
            <Column field="amount" header="Amount" body={amountTemplate} sortable />
            <Column field="createdAt" header="Date" body={dateTemplate} sortable />
          </DataTable>
        )}

        {!loading && pagination.totalCount > 0 && renderPaginationInfo()}
      </div>
    </div>
  );
};

export default BankPayments;
